// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

const trimTrailingSlash = value => (typeof value === 'string' ? value.replace(/\/$/, '') : '');

const isAbsoluteUrl = value => {
  if (typeof value !== 'string' || value.trim() === '') {
    return false;
  }

  try {
    // eslint-disable-next-line no-new
    new URL(value);
    return true;
  } catch (_error) {
    return false;
  }
};

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend.
  if (marketplaceRootURL) {
    return trimTrailingSlash(marketplaceRootURL);
  }

  if (typeof window !== 'undefined' && window?.location?.origin) {
    return trimTrailingSlash(window.location.origin);
  }

  const envBaseUrl = [
    process.env.REACT_APP_MARKETPLACE_ROOT_URL,
    process.env.REACT_APP_CANONICAL_ROOT_URL,
    process.env.RENDER_EXTERNAL_URL,
    process.env.URL,
  ].find(isAbsoluteUrl);

  return envBaseUrl ? trimTrailingSlash(envBaseUrl) : '';
};

export const resolveApiRequestUrl = (path, marketplaceRootURL) => {
  if (typeof path !== 'string' || path.trim() === '') {
    throw new Error('API request path is required.');
  }

  if (isAbsoluteUrl(path)) {
    return path;
  }

  const baseUrl = apiBaseUrl(marketplaceRootURL);
  if (baseUrl) {
    return new URL(path, `${baseUrl}/`).toString();
  }

  if (typeof window !== 'undefined') {
    return path;
  }

  throw new Error(`API base URL could not be resolved for server-side request: ${path}`);
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const { credentials, headers, body, marketplaceRootURL, ...rest } = options;
  const url = resolveApiRequestUrl(path, marketplaceRootURL);

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.text().then(text => {
        let data;
        try {
          data = JSON.parse(text);
        } catch (_e) {
          data = { message: res.statusText || `HTTP ${res.status}` };
        }
        const message = data?.message || data?.error || res.statusText || `HTTP ${res.status}`;
        throw Object.assign(new Error(message), data, {
          status: res.status,
          requestId: res.headers.get('X-Request-ID') || null,
        });
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(text => {
        try {
          return deserialize(text);
        } catch (_e) {
          throw Object.assign(new Error('Invalid transit response'), {
            parseError: true,
            responseBody: text,
          });
        }
      });
    } else if (contentType === 'application/json') {
      return res.text().then(text => {
        try {
          return JSON.parse(text);
        } catch (_e) {
          throw Object.assign(new Error('Invalid JSON response'), {
            parseError: true,
            responseBody: text,
          });
        }
      });
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

const get = (path, queryParams = {}, options = {}) => {
  const queryString = new URLSearchParams(queryParams).toString();
  const pathWithQuery = queryString ? `${path}?${queryString}` : path;

  return request(pathWithQuery, {
    ...options,
    method: methods.GET,
  });
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Resolve listing purchasability from the seller's current private Vacation Mode value.
// The server returns only the minimal availability result and never exposes privateData.
export const listingPurchaseAvailability = body => {
  return post('/api/listing-purchase-availability', body);
};

// Turn the signed-in seller's vacation mode on or off. The server also hides or shows their
// listings in public search.
//
// See `server/api/vacation-mode.js`
export const setVacationMode = body => {
  return post('/api/vacation-mode', body);
};

// Start a sale for the signed-in seller: { percentOff, days, listingIds? } (no ids = whole shop)
export const startSellerSale = body => {
  return post('/api/seller-sale', body);
};

// End the signed-in seller's running sale now
export const endSellerSale = () => {
  return post('/api/seller-sale/end', {});
};

// Reopen the signed-in seller's shop if auto-vacation paused it for inactivity.
export const returnFromAutoVacation = body => {
  return post('/api/vacation-mode/return', body);
};

export const guardedTransaction = body => {
  return post('/api/guarded-transaction', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Seller cancels an unshipped order; the server validates the reason and picks the transition.
export const sellerCancelOrder = body => {
  return post('/api/seller-cancel', body);
};

export const renewListing = body => {
  return post('/api/listings/renew', body);
};

// Initiate a privileged transaction through PayPal checkout flow.
export const initiatePrivilegedWithPaypal = body => {
  return post('/api/paypal/initiate-privileged', body);
};

// Initiate a speculative transaction through PayPal checkout flow.
export const initiateSpecWithPaypal = body => {
  return post('/api/paypal/initiate-spec', body);
};

// Generate PayPal order id and Sharetribe transition payload for checkout flow.
export const getOrderIdWithPaypal = body => {
  return post('/api/paypal/get-order-id', body);
};

// Transition a privileged transaction through PayPal checkout flow.
export const transitionPrivilegedWithPaypal = body => {
  return post('/api/paypal/transition-privileged', body);
};

// Create user through the local API so signup protection runs server-side.
export const createUser = body => {
  return post('/api/auth/create-user', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const verifySignupCaptcha = body => {
  return post('/api/auth/verify-captcha', body);
};

export const queryUsers = queryParams => {
  return get('/api/query-users', queryParams);
};

export const shopSmallUsers = () => {
  return get('/api/shop-small-users', {});
};

export const queryUsersByBookmark = body => {
  return post('/api/query-users-by-bookmark', body);
};

export const queryTransaction = body => {
  return post('/api/query-transaction', body);
};

export const sendMarketplaceMessage = body => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);
  return post('/api/messages/send', body, { signal: controller.signal })
    .catch(error => {
      if (controller.signal.aborted) {
        throw Object.assign(new Error('Message delivery timed out'), {
          code: 'message-send-timeout',
        });
      }
      throw error;
    })
    .finally(() => clearTimeout(timeout));
};

export const processTransactionSmsNotifications = body => {
  if (process.env.REACT_APP_SMS_DEBUG_LOGS === 'true' || process.env.NODE_ENV === 'development') {
    console.log('[transactionSms] direct trigger request', {
      source: body?.source || null,
      transactionId: body?.transactionId?.uuid || body?.transactionId || null,
      transition: body?.transition || null,
    });
  }
  return post('/api/transaction/sms-notifications', body);
};

export const fetchTransactionShippingAddress = body => {
  return post('/api/transaction/shipping-address', body);
};

export const updateTransactionShippingAddress = body => {
  return post('/api/transaction/shipping-address', body);
};

export const showStripeAccount = body => {
  return post('/api/stripe/retrieve-account', body);
};

export const createPayout = body => {
  return post('/api/stripe/create-payout', body);
};

export const addProductToFeed = body => {
  return post('/api/google/add-product-to-feed', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

export const PayPalApi = {
  getOnboardingLink: body => post('/api/paypal/onboarding-link', body),
  getMerchantStatus: merchantId => get('/api/paypal/merchant-status', { merchantId }),
  getMerchantOnboardingStatus: () => get('/api/paypal/merchant-signup-status'),
  createOrder: body => post('/api/paypal/create-order', body),
  captureOrder: body => post('/api/paypal/capture-order', body),
  verifyOrder: body => post('/api/paypal/verify-order', body),
  completeOrder: body => post('/api/paypal/complete-order', body),
  autoCompleteOrder: body => post('/api/paypal/auto-complete', body),
};

export const ShippoApi = {
  createAddress: body => post('/api/shippo/create-address', body),
  getCarriers: body => post('/api/shippo/get-carriers', body),
  getRate: body => post('/api/shippo/get-rate', body),
  createShipment: body => post('/api/shippo/create-shipment', body),
  getTrackingStatus: body => post('/api/shippo/get-tracking-status', body),
  createLabel: body => post('/api/shippo/create-label', body),
  createReturnLabel: body => post('/api/shippo/create-return-label', body),
  markOwnShippingShipped: body => post('/api/shippo/own-shipping/mark-shipped', body),
};

export const PhoneApi = {
  sendVerificationCode: body => post('/api/phone/send-verification-code', body),
  verifyCode: body => post('/api/phone/verify-code', body),
  updatePhone: body => post('/api/phone/update', body),
  optOut: body => post('/api/phone/opt-out', body),
  smsConsent: body => post('/api/phone/sms-consent', body),
};

export const StripeApi = {
  retrieveAccount: showStripeAccount,
  createPayout,
};

// Sharetribe API
// This section handles API calls related to Sharetribe for managing listings.

export const SharetribeApi = {
  /**
   * Update a listing in Sharetribe.
   * @param {Object} body - The data to send for updating the listing.
   * @returns {Promise} - A promise that resolves to the result of the listing update.
   */
  updateListing: body => post('/api/listing/update', body),

  /**
   * Query users in Sharetribe.
   * @param {Object} body - The data to send for querying users.
   * @returns {Promise} - A promise that resolves to the result of the user query.
   */
  queryUsers: body => get('/api/users/query', body),

  /**
   * Query a transaction in Sharetribe.
   * @param {Object} body - The data to send for querying the transaction.
   * @returns {Promise} - A promise that resolves to the result of the transaction query.
   */
  queryTransaction: body => post('/api/query-transaction', body),

  /**
   * Fetch user transaction data and update the user's profile.
   * @param {Object} body - The request payload containing necessary transaction details.
   * @returns {Promise} - A promise that resolves to the response of the API call.
   */
  fetchUserTransactionAndUpdateProfile: body => post('/api/transaction/fetch-and-update', body),

  /**
   * Upload an image to Sharetribe.
   * @param {Object} body - The request payload containing the image to upload.
   * @returns {Promise} - A promise that resolves to the result of the image upload.
   */
  uploadSharetribeImage: body => post('/api/upload-sharetribe-image', body),

  /**
   * Check if a username is available in Sharetribe.
   * @param {string} body - The username to check.
   * @returns {Promise} - A promise that resolves to the result of the username availability check.
   */
  checkUsernameAvailability: body =>
    get(`/api/sharetribe/user/check-availability?username=${body}`),

  /**
   * Fetch a user by username from Sharetribe.
   * @param {string} body - The username to fetch.
   * @returns {Promise} - A promise that resolves to the user entity.
   */
  fetchUserIdByUsername: body => get(`/api/sharetribe/user/${body}`),

  /**
   * Fetch sold items from Sharetribe.
   * @param {Object} body - The request payload containing the transaction ID.
   * @returns {Promise} - A promise that resolves to the sold items.
   */
  fetchSoldItems: body => get(`/api/sharetribe/transaction/${body}`),
};
